<template>
    <div class="">
        <Hero title="Accessibility at Unified.to" colour-index="0"
            subtitle="Last updated: 26 August 2026" :left="true" />

        <div class="privacy px-3 sm:px-28 sm:mt-12 sm:pb-12 text-[var(--black)]">
            <p>
                Unified.to is committed to making our products usable by everyone, including people who rely on
                assistive technologies. We treat accessibility as an ongoing part of how we build and maintain our web
                application, not a one-time project. This statement explains the standard we hold ourselves to, where we
                stand against it today, how we test, and how to reach us if something isn't working for you.
            </p>

            <h5>Scope</h5>
            <p>
                This statement covers the <strong>Unified.to web application at
                    <a href="https://app.unified.to" target="_blank">app.unified.to</a></strong> — the dashboard used
                to configure integrations, manage connections, view logs and webhooks, and administer a workspace.
            </p>
            <p>It does not cover:</p>
            <ul>
                <li>Third-party content displayed inside the application (for example, an integration provider's own
                    authorization screens).</li>
                <li>Our API, SDKs, and developer documentation at <code>docs.unified.to</code>, which are covered under
                    separate, ongoing review.</li>
                <li>Marketing pages on <code>unified.to</code>, which are reviewed on their own cadence.</li>
            </ul>

            <h5>Conformance status</h5>
            <p>
                We measure ourselves against the <strong>Web Content Accessibility Guidelines (WCAG) 2.2, Level
                    AA</strong>, which also encompasses WCAG 2.1 Level AA.
            </p>
            <p>
                In August 2026 we commissioned an independent accessibility audit of
                <code>app.unified.to</code> (Accessibility Conformance Report, VPAT® 2.5Rev International, dated 20
                August 2026) covering twelve representative views: Dashboard, Integrations, Embedded Components, API
                Keys, Settings, Connections, Webhooks, Logs, Event Notifications, Help, Support Tickets, and API
                Explorer. We have since remediated the issues that audit identified across six areas of work (see
                <strong>What we've done</strong> below).
            </p>
            <p>
                <strong>Current status: partially conformant with WCAG 2.2 Level AA, with substantial conformance
                    across the application shell and primary workflows.</strong> "Partially conformant" means most of
                the application meets the standard, and we are actively resolving the remaining gaps described under
                <strong>Known limitations</strong>. We will update this statement as our conformance improves and as we
                re-audit.
            </p>
            <p>The application has also been aligned with the corresponding requirements of:</p>
            <ul>
                <li><strong>Section 508</strong> of the U.S. Rehabilitation Act (which references WCAG 2.0 Level AA).
                </li>
                <li><strong>EN 301 549</strong> (the European accessibility standard for ICT procurement).</li>
            </ul>

            <h5>What we've done</h5>
            <p>Our August 2026 remediation addressed the audit findings in six workstreams:</p>
            <ul>
                <li><strong>Application shell foundations.</strong> The page language is declared, every view has a
                    unique and descriptive title, a "Skip to main content" link is the first stop for keyboard users,
                    and the interface exposes proper landmark regions (a single main area, labelled navigation, one
                    banner) and a consistent heading structure.</li>
                <li><strong>Keyboard operability and visible focus.</strong> Every interactive control is reachable and
                    operable by keyboard and shows a clear, consistently styled focus indicator. Data-grid sorting works
                    from the keyboard, dialogs trap and restore focus and close on <code>Escape</code>, custom tooltips
                    are dismissible, and interactive targets meet minimum size guidance.</li>
                <li><strong>Data tables.</strong> Tables expose correct row and column-header semantics so screen
                    readers announce which column a value belongs to, and they reflow without forcing horizontal page
                    scrolling at 200% text zoom.</li>
                <li><strong>Forms, errors, and status messages.</strong> Every input has a programmatic label, invalid
                    entries are announced with guidance on how to correct them, and important status changes (such as a
                    saved setting or an updated result count) are announced without moving focus.</li>
                <li><strong>Names and text alternatives.</strong> Icon-only buttons and links have descriptive names,
                    and images carry appropriate text alternatives.</li>
                <li><strong>Colour and contrast.</strong> Text, interface controls, and status indicators meet WCAG
                    contrast minimums in both the light and dark themes, and links within text are distinguished by
                    more than colour alone.</li>
            </ul>

            <h5>Known limitations</h5>
            <p>
                We want to be transparent about where work remains. As of the date of this statement, we are aware of
                the following:
            </p>
            <ul>
                <li>Some secondary and less-frequently-used views were outside the twelve-view audit sample and have not
                    yet been individually verified to the same depth; we are extending coverage to them.</li>
                <li>Third-party integration screens rendered during a connection flow are controlled by the integration
                    provider and may not meet the same standard.</li>
                <li>Highly dynamic or data-dense components are subject to continued screen-reader testing as we
                    broaden our automated and manual coverage.</li>
            </ul>
            <p>
                If you encounter a barrier that isn't listed here, please tell us (see <strong>Feedback and
                    contact</strong>) — real-world reports directly shape our priorities.
            </p>

            <h5>How we test</h5>
            <p>Accessibility is verified continuously, not just at release:</p>
            <ul>
                <li><strong>Independent audit.</strong> A third-party expert evaluation against WCAG 2.2 AA (August
                    2026), used as the baseline for remediation.</li>
                <li><strong>Automated checks in continuous integration.</strong> Every change runs an automated
                    accessibility sweep (axe-core) across primary views in both light and dark themes, plus lint rules
                    that block common regressions such as an image without a text alternative or an unnamed control. A
                    change cannot merge if these checks fail.</li>
                <li><strong>Manual testing.</strong> Keyboard-only walkthroughs and screen-reader spot checks (for
                    example, VoiceOver and NVDA) of primary workflows.</li>
            </ul>

            <h5>Compatibility</h5>
            <p>
                The application is designed to work with recent versions of major browsers (Chrome, Edge, Firefox, and
                Safari) and common assistive technologies, including screen readers (such as NVDA, JAWS, and VoiceOver),
                screen magnification, and keyboard-only operation. Accessibility depends in part on the browser and
                assistive technology you use; if you find a combination that behaves poorly, let us know.
            </p>

            <h5>Technical specifications</h5>
            <p>
                Accessibility of <code>app.unified.to</code> relies on the following technologies working with your
                browser and any assistive technologies installed:
            </p>
            <ul>
                <li>HTML</li>
                <li>WAI-ARIA</li>
                <li>CSS</li>
                <li>JavaScript</li>
            </ul>
            <p>These technologies are relied upon for conformance with WCAG 2.2 Level AA.</p>

            <h5>Requesting a VPAT / Accessibility Conformance Report</h5>
            <p>
                A detailed <strong>Voluntary Product Accessibility Template (VPAT® 2.5) / Accessibility Conformance
                    Report (ACR)</strong> documenting our conformance criterion-by-criterion is available to customers
                and prospective customers, including for procurement and public-sector requirements.
            </p>
            <p>
                You can request the current VPAT/ACR — along with our other security and compliance documentation —
                through our <strong>Trust Center at
                    <a href="https://app.mycroft.io/trust/unified-to" target="_blank">app.mycroft.io/trust/unified-to</a></strong>.
            </p>

            <h5>Feedback and contact</h5>
            <p>
                We welcome your feedback on the accessibility of Unified.to. If you experience a barrier, need
                information in a different format, or want to report an issue:
            </p>
            <ul>
                <li><strong>Email:</strong> <a href="mailto:accessibility@unified.to">accessibility@unified.to</a>
                </li>
                <li><strong>Contact form:</strong> <a href="https://unified.to/contact">unified.to/contact</a></li>
                <li><strong>Trust Center</strong> (for accessibility, security, and compliance documentation): <a
                        href="https://app.mycroft.io/trust/unified-to" target="_blank">app.mycroft.io/trust/unified-to</a>
                </li>
            </ul>
            <p>
                Please include the page or feature involved, what you were trying to do, and — if you can — the browser
                and assistive technology you were using. We aim to acknowledge accessibility reports within five business
                days.
            </p>

            <h5>Assessment and this statement</h5>
            <p>
                This statement was prepared on 26 August 2026, based on an independent evaluation of
                <code>app.unified.to</code> conducted in August 2026 and the remediation completed since. We review it
                periodically and after significant changes to the application, and we update the status as our
                conformance evolves.
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { setTitle } from '~~/services/common';

setTitle(
    'Accessibility at Unified.to',
    'Unified.to accessibility statement: partially conformant with WCAG 2.2 Level AA for app.unified.to. Independent audit August 2026, ongoing remediation, and feedback at accessibility@unified.to.'
);
</script>

<style scoped>
h5 {
    margin-bottom: 20px;
    margin-top: 20px;
}

p {
    margin-bottom: 10px;
    color: var(--medium-gray);
}

ul {
    padding-left: 40px;
    margin-top: 20px;
    list-style: disc;
    color: var(--medium-gray);
}

ul li {
    margin-bottom: 10px;
}

a {
    text-decoration: underline;
}

code {
    font-family: monospace;
    font-size: 0.9em;
}
</style>
